import CryptoJS from "crypto-js";

const SECRET_KEY = process.env.NEXT_PUBLIC_ENCRYPTION_KEY || "";

export const encryptData = (data: any): string => {
    if (!SECRET_KEY) {
        console.warn("[Encryption] Encryption key not found. Returning raw data.");
        return typeof data === 'string' ? data : JSON.stringify(data);
    }

    try {
        // Derive key from secret using SHA256 (matches backend middleware expectations)
        const key = CryptoJS.SHA256(SECRET_KEY);

        // Generate a random 16-byte IV
        const iv = CryptoJS.lib.WordArray.random(16);

        // Encrypt the JSON stringified data
        const jsonStr = typeof data === 'string' ? data : JSON.stringify(data);
        const encrypted = CryptoJS.AES.encrypt(jsonStr, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });

        // Format as IV(hex):Ciphertext(hex)
        const ivHex = iv.toString(CryptoJS.enc.Hex);
        const encryptedHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex);

        return `${ivHex}:${encryptedHex}`;
    } catch (error) {
        console.error("[Encryption] Encryption failed:", error);
        return typeof data === 'string' ? data : JSON.stringify(data);
    }
};

export const decryptData = (ciphertext: string): any => {
    console.log("[Encryption] Attempting to decrypt data. Key available:", !!SECRET_KEY);

    if (!SECRET_KEY) {
        console.error("[Encryption] Decryption key missing");
        return null;
    }

    try {
        // Handle Backend Format: IV(hex):Ciphertext(hex)
        if (ciphertext.includes(':')) {
            const parts = ciphertext.split(':');
            if (parts.length === 2) {
                const ivHex = parts[0];
                const encryptedHex = parts[1];

                console.log("[Encryption] Format detected: IV:Ciphertext (Hex)");

                // DERIVE KEY: SHA256(SECRET_KEY)
                // This must match backend's: crypto.createHash('sha256').update(String(SECRET_KEY)).digest()
                const key = CryptoJS.SHA256(SECRET_KEY);

                // PARSE IV
                const iv = CryptoJS.enc.Hex.parse(ivHex);

                // PARSE CIPHERTEXT (Hex -> Base64)
                const encryptedHexWA = CryptoJS.enc.Hex.parse(encryptedHex);
                const encryptedBase64 = CryptoJS.enc.Base64.stringify(encryptedHexWA);

                // DECRYPT
                const decrypted = CryptoJS.AES.decrypt(
                    encryptedBase64,
                    key,
                    {
                        iv: iv,
                        mode: CryptoJS.mode.CBC,
                        padding: CryptoJS.pad.Pkcs7
                    }
                );

                const originalText = decrypted.toString(CryptoJS.enc.Utf8);

                if (originalText) {
                    try {
                        return JSON.parse(originalText);
                    } catch (e) {
                        console.error("[Encryption] Failed to parse decrypted JSON:", e);
                        return originalText;
                    }
                } else {
                    console.error("[Encryption] Decrypted text is empty (Key mismatch? Malformed data?)");
                }
            } else {
                console.error("[Encryption] Invalid format (not 2 parts)");
            }
        } else {
            // Fallback: Standard CryptoJS
            console.log("[Encryption] Fallback to standard decryption");
            const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
            const originalText = bytes.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText);
        }
    } catch (error) {
        console.error("[Encryption] Critical Decryption Failure:", error);
    }

    return null;
};
